import { ApplicationServerService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationToServer, AppToServerBulkDeleteParams,
    AppToServerBulkUpdateParams, SchemaProperty } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, Query } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { deepCopy, generatePluralStr } from '../../common/util';
import DependencyValidationBox, { ResourceTypes } from '../data-quality/DependencyValidationBox';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { APPLICATION_ID_COLUMN, ColumnOrderType, EXCLUDED_APP_SERVER_COLUMNS } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { SchemaPropertyMap } from './types';
import { generateColumnDefs } from './utils';

type AppToServerProps = FlashContextInterface;

const appServerService = new ApplicationServerService();
const schemaService = new SchemaService();
const resourceName = 'app-server-mappings';

const AppToServerPage: FunctionComponent<AppToServerProps> = ({flash}) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;
    const isMounted = useIsMounted();
    const [ items, setItems ] = useState<ApplicationToServer[]>([]);
    const [ schemas, setSchemas ] = useState<SchemaProperty[]>([]);
    const [ schemaMap, setSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ columnDef, setColumnDef ] = useState<ColumnDefinition<ApplicationToServer>[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<ApplicationToServer[]>([]);
    const [ filteredItems, setFilteredItems ] = useState<ApplicationToServer[] | null>(null);
    const [ loading, setLoading ] = useState(true);
    const [ defaultQuery, setDefaultQuery ] = useState<Query>();
    const { updateInlineEditFn } = useContext(InlineEditContext);

    const loadData = async () => {
        setLoading(true);
        setSelectedItems([]);
        try {
            if (!portfolioId) {
                flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                return;
            }
            // Load data
            const appServers = await appServerService.list(portfolioId);
            if (!isMounted()) {
                return;
            }
            appServers.forEach(appServer => {
                appServer.applicationMapped = appServer.application?.inScope ? constants.MAPPED: constants.UNMAPPED;
                appServer.serverMapped = appServer.server?.inScope ? constants.MAPPED: constants.UNMAPPED;
            });
            setItems(appServers);

            // Load schema
            const schemaList = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            const mappedFields: SchemaProperty[] = [{
                value: 'applicationMapped',
                text: constants.APPLICATION_MAPPED,
                messages: {
                    updateForbidden: constants.THIS_FIELD_CANNOT_BE_UPDATED,
                },
            }, {
                value: 'serverMapped',
                text: constants.SERVER_MAPPED,
                messages: {
                    updateForbidden: constants.THIS_FIELD_CANNOT_BE_UPDATED,
                },
            }];
            const newSchemaList = [...schemaList, ...mappedFields];
            setSchemas(newSchemaList);
            const appServerHeaders: SchemaPropertyMap = {};
            newSchemaList.forEach(schema => {
                appServerHeaders[schema.value] = schema;
            });
            setSchemaMap(appServerHeaders);

            // Build column definitions
            const columnDefinitions = generateColumnDefs(appServers, appServerHeaders, ColumnOrderType.APPLICATION_SERVER, EXCLUDED_APP_SERVER_COLUMNS, true);
            setColumnDef(columnDefinitions);
        } catch (err) {
            if (!isMounted()) {
                return;
            }
            flash.alert(FlashType.error, (err as Error).message, constants.APP_SERVER_ERROR_LOADING);
            setItems([]);
            setColumnDef([]);
        } finally {
            if (!isMounted()) {
                return;
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);

    const inlineEditFunction = async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: AppToServerBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            applicationServerIds: [{
                applicationId: (rowItem as ApplicationToServer).applicationId,
                portfolioId,
                serverId: (rowItem as ApplicationToServer).serverId,
            }],
        };

        try {
            await appServerService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.APP_SERVER_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_SERVER_EDITING_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Delete multiple items. If items are selected, they will be deleted. If there are no selected items,
     * then all filtered items will be deleted.
     * @returns void
     */
    const onConfirmBulkDeletion = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            const selectedIds = (selectedItems.length || !filteredItems ? selectedItems : filteredItems)
                .map(selectedItem => ({
                    applicationId: selectedItem.applicationId,
                    portfolioId,
                    serverId: selectedItem.serverId,
                }));
            const conditions: AppToServerBulkDeleteParams = {
                applicationServerIds: selectedIds,
                portfolioId,
            };
            await appServerService.deleteMappings(conditions); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_SERVER_DELETE_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const onConfirmDeleteAll = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            await appServerService.clear(portfolioId); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_SERVER_DELETE_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: string | number | boolean,
    ) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        const selectedSchema = schemaMap[selectedHeader];
        if (!selectedHeader || !selectedSchema) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.APP_SERVER_EDITING_ERROR_HEADING);
            return;
        }
        const itemsToUpdate = selectAll ? (filteredItems ?? items) : selectedItems;
        const selectedIds = itemsToUpdate.map(item => ({
            applicationId: item.applicationId,
            portfolioId,
            serverId: item.serverId,
        }));
        
        const conditions: AppToServerBulkUpdateParams = {
            fieldName: selectedHeader,
            fieldValue: updatedValue,
            portfolioId,
            applicationServerIds: selectedIds,
        };

        try {
            await appServerService.update(conditions);
            const updateSuccessMsg = generatePluralStr(selectedIds.length || items.length, constants.APP_SERVER_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_SERVER_EDITING_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const defaultSortingState: SortingState<ApplicationToServer> = {
        isDescending: false,
        sortingColumn: {
            sortingField: APPLICATION_ID_COLUMN,
        },
    };

    const onMappingFilterChange = (filterBy: string) => {
        setDefaultQuery({
            tokens: [{
                value: constants.UNMAPPED,
                propertyKey: filterBy,
                operator: '='
            }],
            operation: 'and',
        });
    };

    const filterChanged = (items: readonly ApplicationToServer[] | null) => {
        setFilteredItems(deepCopy(items));
    };

    return (
        <SpaceBetween size='m'>
            <DependencyValidationBox
                dependencyType={ResourceTypes.APPLICATION_SERVER}
                dependencyItems={items}
                onFilter={onMappingFilterChange} />
            <AWSCATTable
                resourceName={constants.APP_SERVER_TABLE_HEADER}
                resourceType='applicationServer'
                items={items}
                importWhenEmpty={true}
                columnDefinitions={columnDef}
                loading={loading}
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedItems as () => void}
                onFilterChange={filterChanged}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='applicationServer'
                        resourceName={constants.APP_SERVER_RESOURCE_NAME}
                        resourceCount={items.length}
                        selectionCount={selectedItems.length}
                        hasFilter={!!filteredItems}
                        filteredCount={filteredItems?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={schemas}
                        schemaPropertyMap={schemaMap}
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
                defaultQuery={defaultQuery}
            />
        </SpaceBetween>
    );
};

export default withFlashContext(AppToServerPage);
